import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { ButtonGroup, ToggleButton, Button, Spinner } from "react-bootstrap";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import RatingStars from "components/RatingStars";
import TextInput from "components/TextInput";
import { useAuth } from "context/UserContext";
import {
  createReview,
  fetchSingleReview,
  updateReview,
} from "api/ReviewService";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { getMyReviewList } from "api/ActivityService";

const categories = [
  "Ease of Use",
  "Customer Support",
  "Features",
  "Pricing",
  "Technical Support",
];

const ratings = [
  { name: "1", value: "1" },
  { name: "2", value: "2" },
  { name: "3", value: "3" },
  { name: "4", value: "4" },
  { name: "5", value: "5" },
  { name: "6", value: "6" },
  { name: "7", value: "7" },
  { name: "N/A", value: "N/A" },
];

// Build schema dynamically for subratings
const subRatingSchema = categories.reduce((acc, cat) => {
  acc[cat] = yup.string().required(`${cat.replace(/([A-Z])/g, ' $1')} rating is required`);
  return acc;
}, {});

// Yup schema
const schema = yup.object().shape({
  overallRating: yup
    .number()
    .min(1, "Select a rating")
    .max(5)
    .required("Overall rating is required"),
  title: yup.string().required("Title is required").max(100, "Title must be less than 100 characters").trim(),
  review: yup.string().required("Review content is required") .max(4000, "Review must be less than 4000 characters").trim(),
  subRatings: yup.object().shape(subRatingSchema),
});

const StepThreeComponent = ({ productId, userId, handleNext, step, productName }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const reviewId = location?.state?.reviewId || "";
  const [submitLoader, setSubmitLoader] = useState(false);
  const [defaultValues, setDefaultValues] = useState({
    overallRating: 0,
    title: "",
    review: "",
    subRatings: {},
  });
  window.history.replaceState(null, "", "/write-a-review");
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      overallRating: 0,
      title: "",
      review: "",
      subRatings: {},
    },
  });

  const fetchReview = async () => {
    try {
      const res = await fetchSingleReview({ reviewId }); // API call
      if (res.success) {
        const data = res.body;
        setValue("overallRating", data.overallRating);
        setValue("title", data.title);
        setValue("review", data.review);
        setValue("subRatings", {
          "Ease of Use": data.easeOfUse,
          "Customer Support": data.customerSupport,
          Features: data.features,
          Pricing: data.pricing,
          "Technical Support": data.technicalSupport,
        });
        // setDefaultValues((prev) => ({
        //   ...prev,
        //   overallRating: data.overallRating,
        //   title: data.title,
        //   review: data.review,
        //   subRatings: {
        //     "Ease of Use": "",
        //     "Customer Support": "",
        //     Features: "",
        //     Pricing: "",
        //     "Technical Support": "",
        //   },
        // }));
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      // setLoading(false);
    }
  };

  useEffect(() => {
    if (reviewId !== "") {
      fetchReview();
    }
  }, [reviewId]);

  const subRatings = watch("subRatings");

  const onSubmit = async (data) => {
    try {
      setSubmitLoader(true);
      const verificationId = localStorage.getItem("verificationId") || "";
      
      let payload = {
        userId: userId,
        productId: productId,
        verificationId: verificationId,
        overallRating: data.overallRating,
        title: data.title,
        review: data.review,
        easeOfUse: data.subRatings["Ease of Use"],
        customerSupport: data.subRatings["Customer Support"],
        features: data.subRatings["Features"],
        pricing: data.subRatings["Pricing"],
        technicalSupport: data.subRatings["Technical Support"],
      };
      let res;
      if (reviewId !== "") {
        const updatePlayload = {
          overallRating: data.overallRating,
          title: data.title,
          review: data.review,
          easeOfUse: data.subRatings["Ease of Use"],
          customerSupport: data.subRatings["Customer Support"],
          features: data.subRatings["Features"],
          pricing: data.subRatings["Pricing"],
          technicalSupport: data.subRatings["Technical Support"],
        };

        res = await updateReview(updatePlayload, reviewId);
      } else {
        res = await createReview(payload);
      }

      if (res.success === true && res.code === 200) {
        // toast.success(res.message);
        localStorage.removeItem("step");
        localStorage.removeItem("productId");
        localStorage.removeItem("verificationId");

        if (reviewId !== "") {
          navigate(-1);
        } else {
          handleNext();
        }
      } else {
        toast.error(res.message);
      }
      setSubmitLoader(false);
    } catch (err) {
      setSubmitLoader(false);
      toast.error(err.message);
    }
  };

  return (
    <form className="work_box" onSubmit={handleSubmit(onSubmit)}>
      <h2>Write a review for {productName}</h2>
      <ul className="ul_List">
        <li>
          Your review will only be published after it has been reviewed by our
          team and passed quality checks.
        </li>
        <li>
          This may take up to <strong>3 business days</strong>.
        </li>
        <li>
          If your review is incentivised, it can take up to{" "}
          <strong>1–2 additional business days</strong> to receive it.
        </li>
      </ul>

      <h6>Select Overall Rating</h6>
      <div className="mb-4">
        <Controller
          control={control}
          name="overallRating"
          render={({ field }) => (
            <RatingStars rating={field.value} setRating={field.onChange} />
          )}
        />
        {errors.overallRating && (
          <p className="text-danger">{errors.overallRating.message}</p>
        )}
      </div>

      <Controller
        control={control}
        name="title"
        render={({ field }) => (
          <TextInput
            labelText={"Review Title"}
            type="text"
            placeholder={"Write review title..."}
            errorMessage={errors.title?.message}
            {...field}
          />
        )}
      />

      <Controller
        control={control}
        name="review"
        render={({ field }) => (
          <TextInput
            labelText={"Write Review"}
            textarea="textarea"
            rows={6}
            placeholder={"Write about your experience..."}
            errorMessage={errors.review?.message}
            {...field}
          />
        )}
      />

      <h3 className="mt-4">Sub Ratings</h3>
      {categories.map((category, index) => (
        <div key={index} className="card_blue mb-3">
          <h5>• {category}</h5>
          <Controller
            control={control}
            name={`subRatings.${category}`}
            render={({ field }) => (
              <ButtonGroup>
                {ratings.map((rating, idx) => (
                  <ToggleButton
                    key={`${category}-${idx}`}
                    id={`radio-${category}-${idx}`}
                    type="radio"
                    variant={field.value === rating.value ? "primary" : "light"}
                    name={`radio-${category}`}
                    value={rating.value}
                    checked={field.value === rating.value}
                    onChange={(e) => field.onChange(e.currentTarget.value)}
                    className="mx-1"
                  >
                    {rating.name}
                  </ToggleButton>
                ))}
              </ButtonGroup>
            )}
          />
          {errors?.subRatings?.[category] && (
            <p className="text-danger ms-3">
              {errors.subRatings[category]?.message}
            </p>
          )}
        </div>
      ))}

      <div className="d-flex justify-content-between mt-4">
        <div className="text-end w-100">
          <Button
            variant="info"
            type="submit"
            className="border-radius-100 px-5"
            style={{ minWidth: "180px" }}
            disabled={submitLoader}
          >
            {step === 4 ? (
              "Submit"
            ) : submitLoader ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Continue"
            )}
          </Button>
        </div>
      </div>
      {/* <button type="submit" className="btn btn-primary mt-3">
        Submit Review
      </button> */}
    </form>
  );
};

export default StepThreeComponent;
